import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import CustomButton from '../../../components/CustomButton';
import Permission from '../../../common/permissions';
import { makeHasPermissionSelector } from '../../../selector/authenticationSelector';
import {
    makeIssueReportDetailsUrl,
    makeIssueReportsForBookingUrl,
} from '../../../routes';

interface BookingIssueReportButtonProps {
    /** Route param — the button is not offered without it. */
    orderId: string;
    /** The order's own id, unique per sibling of a multi-order purchase. */
    bookingId?: string | null;
    /** The persisted booking number, shared across siblings. */
    bookingNumber?: string | null;
    /**
     * The booking's problem reports, as the backend attached them
     * (`damageReports`). `undefined` means the booking row has not arrived yet
     * — NOT that there are none.
     */
    damageReports?: { id: string }[] | null;
    /** Opens the create-report flow, prefilled by the booking page. */
    onReportIssue: () => void;
}

/**
 * The booking's problem-report action: it leads to the existing report(s) when
 * there are any, and otherwise offers to file one.
 *
 * It asks nobody. `damageReports` is the answer the backend already computed
 * for this booking — the very field the bookings list renders its "Problem"
 * yes/no column from (`BookingTable.getIssueReportLabel`). Reading it means the
 * badge and this button cannot disagree about the same booking, and no second
 * request is made to re-derive something already on the page.
 */
const BookingIssueReportButton = ({
    orderId,
    bookingId,
    bookingNumber,
    damageReports,
    onReportIssue,
}: BookingIssueReportButtonProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const hasCreatePermission = useSelector(
        makeHasPermissionSelector(Permission.createIssueReports()),
    );
    // Reading a report is not creating one. This is the permission guarding
    // `/issue-reports/:id`, so the link cannot be offered to someone the route
    // would bounce to /unauthorized.
    const hasShowPermission = useSelector(
        makeHasPermissionSelector(Permission.showIssueReports()),
    );

    if (!orderId) {
        return null;
    }

    // The booking row is not loaded yet, so we do not know. Offering "Problem
    // melden" here would claim there is no report; the create action stays
    // available beside the note so a slow or failed load never blocks filing
    // one (#1560/#1562 — secondary reads fail open).
    if (!Array.isArray(damageReports)) {
        return (
            <div className={'flex flex-wrap items-center gap-2'}>
                <span className={'text-sm text-foreground/60'}>
                    {t('booking_details.buttons.issue_report_loading')}
                </span>
                {hasCreatePermission && (
                    <CustomButton
                        title={t('booking_details.buttons.report_issue')}
                        onClick={onReportIssue}
                        isFilled={false}
                    />
                )}
            </div>
        );
    }

    if (hasShowPermission && damageReports.length > 0) {
        const isSingle = damageReports.length === 1;
        return (
            <CustomButton
                title={isSingle
                    ? t('booking_details.buttons.go_to_issue_report')
                    : t('booking_details.buttons.go_to_issue_reports', {
                        count: damageReports.length,
                    })}
                onClick={() => navigate(isSingle
                    // More than one: the call-centre's report and the customer's
                    // own say different things, so jumping to whichever is newer
                    // would hide the other with no hint it exists. Hand over to
                    // the list, filtered to this booking.
                    ? makeIssueReportDetailsUrl(damageReports[0].id)
                    : makeIssueReportsForBookingUrl({
                        orderId: bookingId ?? orderId,
                        bookingNumber,
                    }))}
                isFilled={false}
            />
        );
    }

    if (hasCreatePermission) {
        return (
            <CustomButton
                title={t('booking_details.buttons.report_issue')}
                onClick={onReportIssue}
                isFilled={false}
            />
        );
    }

    return null;
};

export default BookingIssueReportButton;
